import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { FilterOptions, Vehicle } from '@/types';

interface VehiclesState {
  vehicles: Vehicle[];
  selectedVehicle: Vehicle | null;
  filters: FilterOptions;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  loading: boolean;
  error: string | null;
}

const initialState: VehiclesState = {
  vehicles: [],
  selectedVehicle: null,
  filters: {
    search: '',
    status: [],
    type: [],
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
  loading: false,
  error: null,
};

const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    setVehicles: (state, action: PayloadAction<Vehicle[]>) => {
      state.vehicles = action.payload;
    },
    addVehicle: (state, action: PayloadAction<Vehicle>) => {
      state.vehicles.unshift(action.payload);
    },
    updateVehicle: (state, action: PayloadAction<Vehicle>) => {
      const index = state.vehicles.findIndex(v => v.id === action.payload.id);
      if (index !== -1) {
        state.vehicles[index] = action.payload;
      }
      if (state.selectedVehicle?.id === action.payload.id) {
        state.selectedVehicle = action.payload;
      }
    },
    removeVehicle: (state, action: PayloadAction<string>) => {
      state.vehicles = state.vehicles.filter(v => v.id !== action.payload);
      if (state.selectedVehicle?.id === action.payload) {
        state.selectedVehicle = null;
      }
    },
    setSelectedVehicle: (state, action: PayloadAction<Vehicle | null>) => {
      state.selectedVehicle = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<FilterOptions>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setVehicles,
  addVehicle,
  updateVehicle,
  removeVehicle,
  setSelectedVehicle,
  setFilters,
  clearFilters,
  setPagination,
  setLoading,
  setError,
} = vehiclesSlice.actions;

export default vehiclesSlice.reducer;

// Selectors
export const selectVehicles = (state: { vehicles: VehiclesState }) => state.vehicles;
export const selectVehiclesList = (state: { vehicles: VehiclesState }) => state.vehicles.vehicles;
export const selectSelectedVehicle = (state: { vehicles: VehiclesState }) => state.vehicles.selectedVehicle;
export const selectVehiclesLoading = (state: { vehicles: VehiclesState }) => state.vehicles.loading;
export const selectVehiclesError = (state: { vehicles: VehiclesState }) => state.vehicles.error;